import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
    wallet_payout_manual,
    wallet_payout_embedly,
    generate_withdrawal_otp,
} from 'redux/actions/walletActions';
import useGetUserDetails from './useGetUserDetail';

/* ─────────────────────────────────────────────────────────────
   TIER LIMITS — Nigerian KYC compliance
   ───────────────────────────────────────────────────────────── */

export const TIER_LIMITS = {
    TIER_1_MAX: 50_000,    /* NIN only */
    TIER_2_MAX: 150_000,   /* NIN + BVN */
    /* Above 150K = TIER_3 (NIN + BVN + Proof of address) */
};

export const WITHDRAWAL_METHODS = {
    MANUAL:  'manual',   /* Coming soon */
    EMBEDLY: 'embedly',  /* Active, requires OTP */
};

/* Embedly is the only currently working method */
const ACTIVE_METHODS = [WITHDRAWAL_METHODS.EMBEDLY];

const OTP_LENGTH = 4;
const OTP_RESEND_SECONDS = 60;

/* Withdrawal flow steps */
export const FLOW_STEP = {
    IDLE:          'idle',
    METHOD:        'method',
    AMOUNT:        'amount',
    SUMMARY:       'summary',
    OTP:           'otp',
    TIER_UPGRADE:  'tier_upgrade',
    COMING_SOON:   'coming_soon',
    SUCCESS:       'success',
    ERROR:         'error',
};

/**
 * useWithdrawalFlow — state machine for the multi-step withdrawal flow.
 *
 * Owns:
 *   - Current step
 *   - Selected method
 *   - Amount
 *   - OTP + resend timer
 *   - Tier checks (NIN/BVN/Proof)
 *   - Bank account preflight checks
 *   - Success/error final state
 */
const useWithdrawalFlow = ({ wallet, currencySymbol, onComplete }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { flattenedUser } = useGetUserDetails();

    const [step, setStep] = useState(FLOW_STEP.IDLE);
    const [method, setMethod] = useState(null);
    const [amount, setAmount] = useState(0);
    const [otp, setOtp] = useState('');
    const [requestId, setRequestId] = useState('');
    const [resendTimer, setResendTimer] = useState(0);

    /* Operation flags */
    const [sendingOtp, setSendingOtp] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    /* Final state */
    const [resultStatus, setResultStatus] = useState(null);   /* 'success' | 'error' */
    const [resultMessage, setResultMessage] = useState('');
    const [completedAmount, setCompletedAmount] = useState(0);

    /* ── Tier info (derived from user attributes) ───────────── */
    const tierInfo = useMemo(() => {
        const attrs = flattenedUser?.attributes ?? {};
        const hasNIN   = !!attrs.nin && String(attrs.nin).length === 11;
        const hasBVN   = !!attrs.bvn && String(attrs.bvn).length === 11;
        const hasProof = !!attrs.proof_of_address && String(attrs.proof_of_address).trim() !== '';

        let currentTier = 0;
        let currentLimit = 0;
        if (hasNIN) {
            currentTier = 1;
            currentLimit = TIER_LIMITS.TIER_1_MAX;
        }
        if (hasNIN && hasBVN) {
            currentTier = 2;
            currentLimit = TIER_LIMITS.TIER_2_MAX;
        }
        if (hasNIN && hasBVN && hasProof) {
            currentTier = 3;
            currentLimit = Infinity;
        }

        return { hasNIN, hasBVN, hasProof, currentTier, currentLimit };
    }, [flattenedUser]);

    /* ── Resend timer ───────────────────────────────────────── */
    useEffect(() => {
        if (resendTimer <= 0) return;
        const t = setTimeout(() => setResendTimer((v) => v - 1), 1000);
        return () => clearTimeout(t);
    }, [resendTimer]);

    /* ─────────────────────────────────────────────────────────
       FLOW CONTROL
       ───────────────────────────────────────────────────────── */

    const reset = useCallback(() => {
        setStep(FLOW_STEP.IDLE);
        setMethod(null);
        setAmount(0);
        setOtp('');
        setRequestId('');
        setResendTimer(0);
        setSendingOtp(false);
        setSubmitting(false);
        setResultStatus(null);
        setResultMessage('');
        setCompletedAmount(0);
    }, []);

    const close = useCallback(() => {
        reset();
    }, [reset]);

    /* ── Step 1: Click Withdraw → preflight checks ─────────── */
    const startWithdrawal = useCallback(() => {
        /* Balance check */
        if (!wallet?.actual_balance || wallet.actual_balance < 1) {
            toast.error('You have no funds available to withdraw.');
            return;
        }

        /* BVN + DOB preflight (required for any withdrawal) */
        const { bvn, date_of_birth } = flattenedUser?.attributes ?? {};
        if (!bvn && !date_of_birth) {
            toast.error('Please update your BVN and date of birth to continue', { duration: 5000 });
            navigate('/dashboard/profile/paymentinfo');
            return;
        }
        if (!bvn) {
            toast.error('Please update your payment information (BVN) to continue', { duration: 5000 });
            navigate('/dashboard/profile/paymentinfo');
            return;
        }
        if (!date_of_birth) {
            toast.error('Please update your date of birth to continue', { duration: 5000 });
            navigate('/dashboard/profile/user');
            return;
        }

        /* All clear — show method selection */
        setStep(FLOW_STEP.METHOD);
    }, [wallet, flattenedUser, navigate]);

    /* ── Step 2: Pick method ────────────────────────────────── */
    const selectMethod = useCallback((selectedMethod) => {
        /* Manual is coming soon */
        if (!ACTIVE_METHODS.includes(selectedMethod)) {
            setStep(FLOW_STEP.COMING_SOON);
            return;
        }

        setMethod(selectedMethod);
        setAmount(0);
        setStep(FLOW_STEP.AMOUNT);
    }, []);

    /* ── Step 3: Enter amount → enforce tier check ─────────── */
    const submitAmount = useCallback(() => {
        const amt = Number(amount);
        if (!Number.isFinite(amt) || amt < 1) {
            toast.error('Enter a valid amount');
            return;
        }
        if (amt > (wallet?.ledger_balance ?? 0)) {
            toast.error('Amount exceeds available balance');
            return;
        }
        if (amt > tierInfo.currentLimit) {
            /* Tier check fails → show upgrade prompt */
            setStep(FLOW_STEP.TIER_UPGRADE);
            return;
        }

        setStep(FLOW_STEP.SUMMARY);
    }, [amount, wallet, tierInfo]);

    /* ── Step 4 (manual path): Submit manual payout ──────────  */
    const submitManualPayout = useCallback(async () => {
        const attrs = flattenedUser?.attributes ?? flattenedUser ?? {};
        const payload = {
            amount,
            currency: currencySymbol,
            bank_account_id: attrs.embedly_customer_id,
            account_name:    attrs.account_name,
            account_number:  attrs.account_number,
            bank_code:       attrs.bank_code,
        };

        setSubmitting(true);
        try {
            await dispatch(wallet_payout_manual(payload));
            setCompletedAmount(amount);
            setResultStatus('success');
            setResultMessage('Your withdrawal request has been submitted. Processing time: 24–48 hours.');
            setStep(FLOW_STEP.SUCCESS);
        } catch (err) {
            setResultStatus('error');
            setResultMessage(err?.message || 'Failed to process withdrawal. Please try again.');
            setStep(FLOW_STEP.ERROR);
        } finally {
            setSubmitting(false);
            if (typeof onComplete === 'function') onComplete();
        }
    }, [amount, currencySymbol, dispatch, flattenedUser, onComplete]);

    /* ── Step 4 (embedly path): Send OTP ─────────────────────  */
    const sendOtp = useCallback(async () => {
        const attrs = flattenedUser?.attributes ?? flattenedUser ?? {};
        const email = attrs.email;
        const phone = attrs.phone;

        if (!email || !phone) {
            setResultStatus('error');
            setResultMessage('Email or phone number missing from your profile.');
            setStep(FLOW_STEP.ERROR);
            return;
        }

        setSendingOtp(true);
        try {
            const response = await dispatch(generate_withdrawal_otp(email, phone));
            if (response?.success) {
                setRequestId(response.data?.request_id ?? '');
                setOtp('');
                setStep(FLOW_STEP.OTP);
                setResendTimer(OTP_RESEND_SECONDS);
            } else {
                throw new Error(response?.message || 'Failed to send OTP');
            }
        } catch (err) {
            setResultStatus('error');
            setResultMessage(err?.message || 'Failed to send OTP');
            setStep(FLOW_STEP.ERROR);
        } finally {
            setSendingOtp(false);
            
        }
    }, [dispatch, flattenedUser]);

    const resendOtp = useCallback(async () => {
        if (resendTimer > 0 || sendingOtp) return;
        await sendOtp();
    }, [resendTimer, sendingOtp, sendOtp]);

    /* ── Step 5 (embedly path): Submit OTP ───────────────────  */
    const submitEmbedlyPayout = useCallback(async () => {
        if (otp.length !== OTP_LENGTH) {
            toast.error(`Enter the ${OTP_LENGTH}-digit OTP`);
            return;
        }

        const attrs = flattenedUser?.attributes ?? flattenedUser ?? {};

        const payload = {
            amount,
            currency: currencySymbol,
            email:           attrs.email,
            phone:           attrs.phone,
            bank_account_id: attrs.embedly_customer_id,
            bank_code:       attrs.bank_code,
            account_name:    attrs.account_name,
            account_number:  attrs.account_number,
            bank_name:       attrs.bank_name,
            first_name:      attrs.first_name,
            last_name:       attrs.last_name,
            middle_name:     attrs.middle_name,
            date_of_birth:   attrs.date_of_birth,
            gender:          attrs.gender,
            address:         attrs.address,
            city:            attrs.city,
            state:           attrs.state,
            country:         attrs.country,
            nin:             attrs.nin,
            bvn:             attrs.bvn,
            proof_of_address: attrs.proof_of_address,
        };

        setSubmitting(true);
        try {
            await dispatch(wallet_payout_embedly(payload, otp, requestId));
            setCompletedAmount(amount);
            setResultStatus('success');
            setResultMessage('Your withdrawal has been submitted successfully.');
            setOtp('');
            setRequestId('');
            setStep(FLOW_STEP.SUCCESS);
        } catch (err) {
            setResultStatus('error');
            setResultMessage(err?.message || 'Failed to process withdrawal. Please try again.');
            setStep(FLOW_STEP.ERROR);
        } finally {
            setSubmitting(false);
            if (typeof onComplete === 'function') onComplete();
        }
    }, [amount, otp, requestId, currencySymbol, dispatch, flattenedUser, onComplete]);

    /* ── From Summary → either submit manual or send OTP ────  */
    const confirmSummary = useCallback(() => {
        if (method === WITHDRAWAL_METHODS.MANUAL) {
            submitManualPayout();
        } else {
            sendOtp();
        }
    }, [method, submitManualPayout, sendOtp]);

    /* ── Navigate to verification page ──────────────────────  */
    const goToVerification = useCallback(() => {
        reset();
        navigate('/dashboard/profile/paymentinfo');
    }, [navigate, reset]);

    /* ── Step navigation helpers ────────────────────────────  */
    const goBack = useCallback(() => {
        switch (step) {
            case FLOW_STEP.AMOUNT:       setStep(FLOW_STEP.METHOD); break;
            case FLOW_STEP.SUMMARY:      setStep(FLOW_STEP.AMOUNT); break;
            case FLOW_STEP.OTP:          setStep(FLOW_STEP.SUMMARY); break;
            case FLOW_STEP.TIER_UPGRADE: setStep(FLOW_STEP.AMOUNT); break;
            case FLOW_STEP.COMING_SOON:  setStep(FLOW_STEP.METHOD); break;
            default: break;
        }
    }, [step]);

    return {
        /* State */
        step,
        method,
        amount,
        otp,
        resendTimer,
        sendingOtp,
        submitting,
        resultStatus,
        resultMessage,
        completedAmount,
        tierInfo,

        /* Setters */
        setAmount,
        setOtp,

        /* Actions */
        startWithdrawal,
        selectMethod,
        submitAmount,
        confirmSummary,
        submitEmbedlyPayout,
        resendOtp,
        goToVerification,
        goBack,
        close,
        reset,

        /* Method constants for the UI */
        WITHDRAWAL_METHODS,
        ACTIVE_METHODS,
        TIER_LIMITS,
        OTP_LENGTH,
    };
};

export default useWithdrawalFlow;